/**
 * The inbox payloads, as `backend-notification` serves them.
 *
 * **Field names are snake_case on the wire.** This service serialises without
 * the camelCase alias generator its seven peers apply, and the console follows
 * the callee rather than the convention — the same rule `@shared/app-shell`
 * already records for `unread_count`.
 *
 * These are local declarations only because `src/shared/api/generated/` is still
 * empty; once `pnpm codegen` has run against the notification OpenAPI document
 * they are replaced by the generated DTOs and this file keeps only what the
 * surface itself adds.
 */

/**
 * The four subject types of the owning service's closed set. They are carried
 * because the payload carries them — **nothing on this surface addresses one**.
 * Row activation navigating to a notification's subject is a `[ ]` row, so there
 * is no route template, no resolver, and no `cta` field to resolve against.
 */
export type SubjectType = 'SUBSCRIPTION' | 'CONTENT_ITEM' | 'COMMENT' | 'CASE';

/**
 * One inbox row, body included. The list carries every body in full, which is
 * what makes the detail panel a disclosure rather than a second request.
 */
export interface NotificationRow {
  id: string;
  event_type: string;
  subject_type: SubjectType;
  subject_id: string;
  headline: string;
  body: string;
  /** ISO-8601 UTC, ASCII. The sort and the `<time datetime>` both read this. */
  occurred_at: string;
  /** ISO-8601 UTC when read, `null` while unread. The state cell reads it. */
  read_at: string | null;
}

/**
 * A keyset page. **There is no `total`** — the inbox is a hot list and the
 * owning service refuses the count query per page that a page-of-N line would
 * need. What the pager can state is the page it is on and how many rows it is
 * showing, and that is what it states.
 */
export interface NotificationPage {
  items: NotificationRow[];
  page_size: number;
  has_next: boolean;
  next_cursor: string | null;
}

/** Every bulk action answers with its own count and the fresh unread figure. */
export interface MarkResult {
  updated_count: number;
  unread_count: number;
}

export interface DeleteResult {
  deleted_count: number;
  unread_count: number;
}

/**
 * The three modes of the sort control.
 *
 * Two are date orders the service performs — `-createdAt` and `createdAt` — and
 * the third is not a date order at all. «Unread first» partitions the page the
 * operator is holding, because the surface's whole purpose is outstanding work
 * and no sort key on the wire expresses it. It is a **view** of the page, never
 * a filter: filtering to unread would drop the read rows, which is a different
 * question than the one the control asks.
 */
export type SortMode = 'date-desc' | 'date-asc' | 'unread';

/** What the wire accepts. The third mode rides on the newest-first order. */
export const WIRE_SORT: Readonly<Record<SortMode, '-createdAt' | 'createdAt'>> = {
  'date-desc': '-createdAt',
  'date-asc': 'createdAt',
  unread: '-createdAt',
};

export function isUnread(row: NotificationRow): boolean {
  return row.read_at === null;
}
